/* ===================================================================
   Tutorials -- PIXEL-FAITHFUL rebuild of the Claude Design mockup
   ("Rhody Reviews.dc.html", TUTORIALS screen; desktop 1451-1484, mobile
   2573-2593). Corbato, 2026-07-03.

   Deliberate, literal reproduction of the mockup's visual output:
     - Sticky header with title + "Run full setup" (primary) and "Take the
       tour" (ghost) actions.
     - "Getting started - 6 guides" eyebrow.
     - A 2-up grid of six guide cards, each: a numbered tile, title,
       description, a "Show me how" CTA, and a duration hint.
     - A chat-only support footer (HARD RULE: no phone number anywhere).
   Styling lives in styles/tutorials.css, values lifted verbatim from the
   mockup's inline styles.

   TOUR WIRING (Pass C hook).
   Each card's "Show me how" is meant to launch the interactive product tour
   at that card's step (the tour CONTROLLER is a later Pass C deliverable, not
   built here). We leave the correct, stable hooks so Pass C connects them
   without touching this file's structure:
     - `startTour(step?)` is a single callback. Today it dispatches a
       `rhody:start-tour` CustomEvent on window carrying the step, so a Pass C
       controller can `addEventListener` and drive from there. Until that
       listener exists it is a harmless no-op (no throw, no navigation).
     - Every "Show me how" carries `data-tour-step={num}`; the header "Take the
       tour" carries `data-tour-step` unset (start from step 1). Keep these
       attributes intact -- Pass C keys off them.

   VIDEO / "Watch guide" (HANDOFF section 2).
   Tutorials exposes ONLY "Show me how" (the interactive tour), not a video
   button. We do NOT render a video button or fabricate any video URL -- there
   are no videos yet, so we do not show a video control at all.

   HARD RULE: support is chat-only. The footer's only action starts a chat;
   there is no phone number anywhere on this screen.
   =================================================================== */
import { useNavigate } from 'react-router-dom';
import '../styles/tutorials.css';

/* The six "Getting started" guides, verbatim titles/descriptions/durations
   from the prototype's tutorials demo data. */
const GUIDES: { num: number; title: string; desc: string; mins: string }[] = [
  { num: 1, title: 'Connect your Google Business Profile', desc: 'Link Google so new reviews flow in and approved replies post back out.', mins: '2 min' },
  { num: 2, title: 'Add your first customers', desc: 'Import a CSV or add a customer by hand, then send your first request.', mins: '3 min' },
  { num: 3, title: 'Set your reply voice', desc: 'Pick the tone Rhody drafts your replies in. You approve every one.', mins: '2 min' },
  { num: 4, title: 'Turn on automations', desc: 'Let requests go out automatically after a job. Sends only -- never auto-posts.', mins: '3 min' },
  { num: 5, title: 'Add the review widget to your site', desc: 'Drop in one snippet to showcase your best reviews on your website.', mins: '2 min' },
  { num: 6, title: 'Set up private feedback', desc: 'Offer customers a private channel alongside the public review link.', mins: '2 min' },
];

/* Check-in-circle glyph reused by every "Show me how" and the header tour
   button (matches the prototype's inline SVG). */
function TourIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M9 11l3 3L22 4" />
      <path d="M12 2a10 10 0 1 0 10 10" />
    </svg>
  );
}

export function Tutorials() {
  const navigate = useNavigate();

  /* "Run full setup" -> the onboarding flow (existing route). */
  const openOnboarding = () => navigate('/onboarding');

  /* Pass C tour hook. Dispatches a window CustomEvent the Pass C tour
     controller listens for; a harmless no-op until that listener mounts.
     `step` is undefined for "Take the tour" (start at the beginning) or the
     card number for a card's "Show me how". */
  const startTour = (step?: number) => {
    window.dispatchEvent(new CustomEvent('rhody:start-tour', { detail: { step } }));
  };

  return (
    <div className="rhf-tut" data-tour="tutorials">
      {/* STICKY HEADER (desktop) */}
      <div className="rht-head">
        <div>
          <h1 className="rht-head__h">Tutorials</h1>
          <p className="rht-head__p">Short, step-by-step guides for setting up and getting the most out of Rhody.</p>
        </div>
        <div className="rht-head__tools">
          <button type="button" className="rht-btn rht-btn--primary" onClick={openOnboarding}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M5 3l14 9-14 9V3z" />
            </svg>
            Run full setup
          </button>
          <button
            type="button"
            className="rht-btn rht-btn--ghost"
            data-tour-step
            onClick={() => startTour()}
          >
            <TourIcon size={16} />
            Take the tour
          </button>
        </div>
      </div>

      <div className="rht-body">
        {/* MOBILE HEADER (inline; hidden on desktop) */}
        <div className="rht-mhead">
          <div className="rht-mhead__h">Tutorials</div>
          <div className="rht-mhead__s">Step-by-step setup guides</div>
        </div>
        {/* Full-width "Run full setup" -- mobile only */}
        <button type="button" className="rht-msetup" onClick={openOnboarding}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M5 3l14 9-14 9V3z" />
          </svg>
          Run full setup
        </button>

        <div className="rht-eyebrow">Getting started &middot; {GUIDES.length} guides</div>

        <div className="rht-grid">
          {GUIDES.map((g) => (
            <div key={g.num} className="rht-card">
              <div className="rht-card__num">{g.num}</div>
              <div className="rht-card__body">
                <div className="rht-card__titlerow">
                  <span className="rht-card__title">{g.title}</span>
                </div>
                <div className="rht-card__desc">{g.desc}</div>
                <div className="rht-card__foot">
                  {/* Show me how -> launch the tour at THIS card's step.
                      Pass C keys off data-tour-step; startTour() is the stable
                      hook (no-op until the controller lands). */}
                  <button
                    type="button"
                    className="rht-card__cta"
                    data-tour-step={g.num}
                    onClick={() => startTour(g.num)}
                  >
                    <TourIcon />
                    Show me how
                  </button>
                  <span className="rht-card__mins">{g.mins}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ASYNC SUPPORT FOOTER -- HARD RULE: no phone number anywhere.
            AUDIT C4: "Start a chat" was a dead button. Interim: a real
            mailto until the LiveChat affiliate widget is wired (needs
            Steve's LiveChat account). Copy matches what the link does. */}
        <div className="rht-help">
          <svg className="rht-help__ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth={2}>
            <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.7-.8L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z" />
          </svg>
          <div className="rht-help__text">Still stuck? Email support and Rhody Reviews gets back to you fast -- no phone tag, no tickets.</div>
          <a
            className="rht-help__btn"
            style={{ textDecoration: 'none' }}
            href="mailto:support@rhodyreviews.com?subject=Rhody%20Reviews%20support"
          >
            Email support
          </a>
        </div>
      </div>
    </div>
  );
}
